import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Chip,
  InputAdornment,
  IconButton,
  Skeleton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ClearIcon from "@mui/icons-material/Clear";
import SecurityIcon from "@mui/icons-material/Security";
import MFASettings from "./MFASettings";
import { Switch } from "@mui/material";
import api from "./../utils/api";
import { formatToLocalTime } from "../../src/utils/formatToLocalTime";

/* Images are served from the server root (not /api), so strip the /api suffix */
const SERVER_BASE = "https://heritagespringscommunity.myhoa.club";
const toImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${SERVER_BASE}${path}`;
};

const DetailItem = ({ icon, label, value, valueColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: "14px",
      border: "1px solid rgba(47,99,173,0.08)",
      backgroundColor: "rgba(255,255,255,0.96)",
      boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
      height: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
      <Box sx={{ color: "#2f63ad", display: "flex", alignItems: "center" }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ color: "#6b778c", fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>

    <Typography
      variant="body1"
      sx={{
        color: valueColor || "#223142",
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const UserProfile = () => {
  const location = useLocation();

  const [userData, setUserData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [forceResetPassword, setForceResetPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);

  const [editForm, setEditForm] = useState({
    Name: "",
    Lastname: "",
    Email: "",
  });

  const [resetForm, setResetForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const fileInputRef = useRef(null);

  /* ── MFA state ── */
  const [mfaStatus, setMfaStatus]             = useState(null);   // { enabled, emailHint }
  const [mfaDialogOpen, setMfaDialogOpen]     = useState(false);
  const [mfaPassword, setMfaPassword]         = useState("");
  const [showMfaPassword, setShowMfaPassword] = useState(false);
  const [mfaSaving, setMfaSaving]             = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const forceReset = params.get("forceReset");

    if (forceReset === "true") {
      setForceResetPassword(true);
      setResetOpen(true);
      setResetStep(1);
    }
  }, [location.search]);

  useEffect(() => {
    const username = localStorage.getItem("username");

    if (!username) {
      setProfileError("Could not determine the logged-in user. Please sign out and sign back in.");
      setLoadingProfile(false);
      return;
    }

    const applyData = (data) => {
      setUserData(data);
      setEditForm({
        Name: data.Name || "",
        Lastname: data.Lastname || "",
        Email: data.Email || "",
      });
    };

    api
      .get(`/users/${username}`)
      .then((res) => applyData(res.data))
      .catch(() =>
        // Fallback: fetch via JWT-authenticated /profile endpoint
        api
          .get("/profile")
          .then((res) => applyData(res.data))
          .catch((err) => {
            console.error("Failed to fetch user profile:", err);
            setProfileError("Unable to load your profile. Please try refreshing the page.");
          })
      )
      .finally(() => setLoadingProfile(false));
  }, []);

  /* Fetch MFA status once profile is loaded */
  useEffect(() => {
    if (!userData) return;
    api.get("/user/mfa/status")
      .then((res) => setMfaStatus(res.data))
      .catch(() => { /* silent. feature may not be enabled for this user */ });
  }, [userData]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const openMfaDialog = () => { setMfaPassword(""); setShowMfaPassword(false); setMfaDialogOpen(true); };

  const handleMfaConfirm = async () => {
    if (!mfaPassword) return;
    setMfaSaving(true);
    const enabling = !mfaStatus?.enabled;
    try {
      await api.put("/user/mfa", { enabled: enabling, password: mfaPassword });
      setMfaStatus((prev) => ({ ...prev, enabled: enabling }));
      setMfaDialogOpen(false);
      showSnackbar(`Two-factor authentication ${enabling ? "enabled" : "disabled"}.`);
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to update MFA setting.", "error");
    } finally {
      setMfaSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showSnackbar("Image must be smaller than 2 MB.", "error");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("profile_picture", file);
      const res = await api.post(`/users/${userData.UserID}/profile-picture`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newPath = res.data.profile_picture ?? res.data.profile_picture_url ?? res.data.url;
      setUserData((prev) => ({ ...prev, profile_picture: newPath }));
      setPhotoVersion((v) => v + 1);
      showSnackbar("Profile picture updated.");
    } catch {
      showSnackbar("Failed to upload photo.", "error");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    try {
      await api.delete(`/users/${userData.UserID}/profile-picture`);
      setUserData((prev) => ({ ...prev, profile_picture: null }));
      setPhotoVersion((v) => v + 1);
      showSnackbar("Profile picture removed.");
    } catch {
      showSnackbar("Failed to remove photo.", "error");
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/profile/${userData.UserID}`, {
        Name: editForm.Name,
        Lastname: editForm.Lastname,
        Email: editForm.Email,
      });

      setUserData({ ...userData, ...editForm });
      setEditOpen(false);
      showSnackbar("Profile updated successfully.");
    } catch (err) {
      console.error("Failed to update profile:", err);
      showSnackbar("Error updating profile.", "error");
    }
  };

  const closeResetDialog = () => {
    if (forceResetPassword) {
      showSnackbar("Please reset your password before continuing.", "warning");
      return;
    }

    setResetOpen(false);
    setResetForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetStep(1);
  };

  const resetPasswordState = () => {
    setResetOpen(false);
    setForceResetPassword(false);
    setResetForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetStep(1);
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  if (loadingProfile) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 1100,
            borderRadius: "20px",
            border: "1px solid rgba(47,99,173,0.08)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
            backgroundColor: "rgba(255,255,255,0.96)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
            {/* Left panel skeleton */}
            <Box
              sx={{
                width: { xs: "100%", md: "32%" },
                p: { xs: 3, md: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRight: { xs: "none", md: "1px solid rgba(47,99,173,0.08)" },
              }}
            >
              <Skeleton variant="circular" width={104} height={104} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="60%" height={28} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="45%" height={20} sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Skeleton variant="rounded" width={70} height={26} />
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton variant="rounded" width={110} height={36} />
                <Skeleton variant="rounded" width={130} height={36} />
              </Box>
            </Box>

            {/* Right panel skeleton */}
            <Box sx={{ width: { xs: "100%", md: "68%" }, p: { xs: 3, md: 4 } }}>
              <Skeleton variant="text" width="40%" height={34} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Skeleton variant="rounded" height={80} sx={{ borderRadius: "14px" }} />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Skeleton variant="rounded" height={80} sx={{ borderRadius: "14px" }} />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (profileError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, md: 3 } }}>
        <Alert
          severity="error"
          sx={{ maxWidth: 600, width: "100%", borderRadius: "14px", fontSize: "0.95rem" }}
        >
          {profileError}
        </Alert>
      </Box>
    );
  }

  const fullName = `${userData.Name || ""} ${userData.Lastname || ""}`.trim();
  const rolesText = userData.Roles?.map((r) => r.RoleName).join(", ") || "No Role";

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1100,
          borderRadius: "20px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
          backgroundColor: "rgba(255,255,255,0.96)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
          <Box
            sx={{
              width: { xs: "100%", md: "32%" },
              background:
                "linear-gradient(180deg, rgba(47,99,173,0.06), rgba(47,99,173,0.02))",
              borderRight: { xs: "none", md: "1px solid rgba(47,99,173,0.08)" },
              borderBottom: { xs: "1px solid rgba(47,99,173,0.08)", md: "none" },
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              hidden
              onChange={handlePhotoUpload}
            />

            {/* Clickable avatar with camera overlay */}
            <Box sx={{ position: "relative", mb: 2 }}>
              <Tooltip title="Click to change photo" placement="bottom">
                <Box
                  onClick={() => !uploadingPhoto && fileInputRef.current?.click()}
                  sx={{
                    position: "relative",
                    width: 104, height: 104,
                    borderRadius: "50%",
                    cursor: uploadingPhoto ? "default" : "pointer",
                    "&:hover .avatar-overlay": { opacity: 1 },
                  }}
                >
                  <Avatar
                    src={userData.profile_picture
                      ? `${toImgUrl(userData.profile_picture)}?v=${photoVersion}`
                      : undefined}
                    sx={{
                      width: 104, height: 104,
                      fontSize: "2rem", fontWeight: 700,
                      backgroundColor: "#2f63ad",
                      boxShadow: "0 8px 20px rgba(47,99,173,0.28)",
                    }}
                  >
                    {!userData.profile_picture && (userData.Name?.charAt(0) || "U")}
                  </Avatar>

                  {/* Hover / loading overlay */}
                  <Box
                    className="avatar-overlay"
                    sx={{
                      position: "absolute", inset: 0,
                      borderRadius: "50%",
                      backgroundColor: "rgba(0,0,0,0.48)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      opacity: uploadingPhoto ? 1 : 0,
                      transition: "opacity 0.2s",
                    }}
                  >
                    {uploadingPhoto
                      ? <CircularProgress size={26} sx={{ color: "#fff" }} />
                      : <CameraAltIcon sx={{ color: "#fff", fontSize: 28 }} />
                    }
                  </Box>
                </Box>
              </Tooltip>

              {/* Remove photo button */}
              {userData.profile_picture && !uploadingPhoto && (
                <Tooltip title="Remove photo">
                  <IconButton
                    size="small"
                    onClick={handleRemovePhoto}
                    sx={{
                      position: "absolute", top: 2, right: 2,
                      width: 22, height: 22,
                      backgroundColor: "#fff",
                      border: "1.5px solid rgba(0,0,0,0.12)",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                      "&:hover": { backgroundColor: "#fce8e8", borderColor: "#d32f2f" },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 13, color: "#d32f2f" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
              {fullName || "User"}
            </Typography>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.5 }}>
              {userData.Username}
            </Typography>

            <Typography
              variant="caption"
              onClick={() => !uploadingPhoto && fileInputRef.current?.click()}
              sx={{
                color: "#9aa5b4", cursor: "pointer", mb: 1,
                "&:hover": { color: "#2f63ad", textDecoration: "underline" },
              }}
            >
              {userData.profile_picture ? "Change photo" : "Add profile photo"}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, mb: 2 }}>
              {(userData.Roles?.length ? userData.Roles : [{ RoleName: "No Role" }]).map(
                (role, index) => (
                  <Chip
                    key={`${role.RoleName}-${index}`}
                    label={role.RoleName}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "rgba(47,99,173,0.08)",
                      color: "#2f63ad",
                      fontWeight: 600,
                    }}
                  />
                )
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", justifyContent: "center", mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                sx={primaryButtonSx}
                onClick={() => setEditOpen(true)}
              >
                Edit Profile
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<LockResetIcon />}
                sx={secondaryButtonSx}
                onClick={() => setResetOpen(true)}
              >
                Reset Password
              </Button>
            </Box>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "68%" }, p: { xs: 3, md: 4 } }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                Profile Details
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Review and manage your account information.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailItem icon={<PersonOutlineIcon fontSize="small" />} label="Full Name" value={fullName || "N/A"} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem icon={<EmailOutlinedIcon fontSize="small" />} label="Email" value={userData.Email || "N/A"} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<VerifiedUserIcon fontSize="small" />}
                  label="Status"
                  value={userData.Status || "N/A"}
                  valueColor={userData.Status?.toLowerCase() === "active" ? "#2e7d32" : "#d32f2f"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem icon={<VerifiedUserIcon fontSize="small" />} label="Roles" value={rolesText} />
              </Grid>

              <Grid item xs={12}>
                <DetailItem
                  icon={<AccessTimeIcon fontSize="small" />}
                  label="Last Login"
                  value={userData.LastLogin ? formatToLocalTime(userData.LastLogin) : "Never"}
                />
              </Grid>
            </Grid>

            {/* ── Two-Factor Authentication. new multi-method settings ── */}
            <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid rgba(47,99,173,0.08)" }}>
              <MFASettings />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.35rem", pt: 3, pb: 1, color: "#1f1f1f" }}>
          Edit Profile
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ mt: 0.5, mb: 1, textAlign: "center", color: "#666", fontSize: "0.95rem" }}>
            Update your profile information below.
          </Typography>

          <TextField fullWidth label="First Name" value={editForm.Name} onChange={(e) => setEditForm({ ...editForm, Name: e.target.value })} sx={fieldSx} />

          <TextField fullWidth label="Last Name" value={editForm.Lastname} onChange={(e) => setEditForm({ ...editForm, Lastname: e.target.value })} sx={fieldSx} />

          <TextField fullWidth label="Email" type="email" value={editForm.Email} onChange={(e) => setEditForm({ ...editForm, Email: e.target.value })} sx={fieldSx} />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSave} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={closeResetDialog} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.35rem", pt: 3, pb: 1, color: "#1f1f1f" }}>
          {forceResetPassword ? "Password Reset Required" : "Reset Password"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {forceResetPassword && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
              This is your first login. Please reset your password before continuing.
            </Alert>
          )}

          <Typography sx={{ mt: 0.5, mb: 1, textAlign: "center", color: "#666", fontSize: "0.95rem" }}>
            {resetStep === 1
              ? "Verify your current password to continue."
              : "Enter and confirm your new password."}
          </Typography>

          {resetStep === 1 ? (
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? "text" : "password"}
              value={resetForm.currentPassword}
              onChange={(e) => setResetForm({ ...resetForm, currentPassword: e.target.value })}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={resetForm.newPassword}
                onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={resetForm.confirmPassword}
                onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {resetForm.confirmPassword && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: resetForm.newPassword === resetForm.confirmPassword ? "#2e7d32" : "#d32f2f",
                      fontWeight: 600,
                    }}
                  >
                    {resetForm.newPassword === resetForm.confirmPassword
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          {!forceResetPassword && (
            <Button onClick={closeResetDialog} variant="outlined" sx={secondaryButtonSx}>
              Cancel
            </Button>
          )}

          {resetStep === 1 ? (
            <Button
              variant="contained"
              sx={primaryButtonSx}
              onClick={async () => {
                try {
                  await api.post("/verify-current-password", {
                    currentPassword: resetForm.currentPassword,
                  });
                  setResetStep(2);
                } catch (err) {
                  showSnackbar("Current password is incorrect.", "error");
                }
              }}
              disabled={!resetForm.currentPassword}
            >
              Verify
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={primaryButtonSx}
              onClick={async () => {
                if (resetForm.newPassword !== resetForm.confirmPassword) {
                  showSnackbar("Passwords do not match.", "error");
                  return;
                }

                try {
                  await api.post("/reset-own-password", {
                    currentPassword: resetForm.currentPassword,
                    newPassword: resetForm.newPassword,
                  });

                  resetPasswordState();
                  showSnackbar("Password updated successfully.");
                } catch (err) {
                  showSnackbar(err.response?.data?.message || "Password reset failed.", "error");
                }
              }}
              disabled={
                !resetForm.newPassword ||
                !resetForm.confirmPassword ||
                resetForm.newPassword !== resetForm.confirmPassword
              }
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── MFA enable / disable confirmation dialog ── */}
      <Dialog open={mfaDialogOpen} onClose={() => setMfaDialogOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.2rem", pt: 3, pb: 1, color: "#1f1f1f" }}>
          {mfaStatus?.enabled ? "Disable Two-Factor Authentication" : "Enable Two-Factor Authentication"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: "50%", mx: "auto", mb: 1.5,
                backgroundColor: mfaStatus?.enabled ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <SecurityIcon sx={{ fontSize: 24, color: mfaStatus?.enabled ? "#dc2626" : "#16a34a" }} />
            </Box>
            <Typography variant="body2" sx={{ color: "#6b778c", fontSize: "0.92rem", lineHeight: 1.55 }}>
              {mfaStatus?.enabled
                ? "Disabling two-factor authentication will make your account less secure. Confirm with your password to continue."
                : "When enabled, a verification code will be sent to your email each time you sign in."}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Confirm your password"
            type={showMfaPassword ? "text" : "password"}
            value={mfaPassword}
            onChange={(e) => setMfaPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && mfaPassword) handleMfaConfirm(); }}
            autoFocus
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowMfaPassword((p) => !p)} onMouseDown={(e) => e.preventDefault()} edge="end">
                    {showMfaPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={() => setMfaDialogOpen(false)} variant="outlined" sx={secondaryButtonSx} disabled={mfaSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMfaConfirm}
            disabled={!mfaPassword || mfaSaving}
            sx={{
              ...primaryButtonSx,
              ...(mfaStatus?.enabled && {
                backgroundColor: "#dc2626",
                "&:hover": { backgroundColor: "#b91c1c" },
              }),
            }}
          >
            {mfaSaving
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : mfaStatus?.enabled ? "Disable 2FA" : "Enable 2FA"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;